// @ts-nocheck
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { toast } from 'react-toastify';
import InfoIcon from '@mui/icons-material/Info';
import { useFormik } from 'formik';

import {
  FHAntdDateTimePicker,
  FHButton,
  FCheckbox,
  FHSelect,
  FHInputField,
  FProgressBar,
  FHTooltip,
  FHConfirmDialog,
  FHAutocomplete,
  FAntdTimePicker,
  FBoxContainer,
} from '../../../base';
import { providerAssignmentActions, providerActions } from '../../../../store';
import { clientSupervisionActions } from '../../../../store/supervision-notes/clientSupervisions.slice';
import { useClientSupervisionEditable, useProcedureTimeRestrictions } from '../../../../hooks';
import {
  areUserRolesIn,
  capitalizeFirstLetter,
  combineMomentDateTime,
  disabledDatesOutsideRange,
  disabledFutureDate,
  disabledFutureHoursAndMinutes,
  history,
} from '../../../../helpers';
import { getUtcOffset } from '../../../../utils/common';
import { FORTA_BLACK, FORTA_BLUE, FORTA_ERROR, FORTA_WHITE } from '../../../../utils/colors';
import {
  BackgroundTypes,
  ClinicalRelationships,
  Roles,
  SupervisionNoteStatus,
  Themes,
} from '../../../../constants';
import {
  CLIENT_SN_DURATION_OPTIONS,
  SUPERVISION_LOCATION_V2,
  SUPERVISION_SETTING_V2,
} from '../constants';
import {
  isStartClientSupervisionButtonDisabled,
  resolveClientIdFromFormOrSupervision,
  shouldShowNoActiveBtsMessage,
} from './utils/general';
import { clientSupervisionSchema } from './utils/validationSchema';
import AttentionIcon from '../../../../assets/images/attention.svg';
import TrashIcon from '../../../svgs/TrashIcon3';
import DownloadIcon from '../../../svgs/DownloadButton';
import ClientTimezoneDisplay from '../../ClientTimezoneDisplay';
import { ClinicalStatus } from '../../../../admin/constants/general';
import BehavioralTechnicianSection from './components/behavioralTechnicianSection';

import type { Moment } from 'moment';
import type { ChangeEvent, HTMLAttributes } from 'react';
import type { RootState } from '../../../../store';
import type {
  IClientSupervision,
  IOption,
  IProvider,
  IProviderAssignment,
  SelectValueType,
  Bays,
} from '../../../../utils/types';
interface ClientSupervisionInfoProps {
  clientSupervision: IClientSupervision | null;
  onUpdate: (data: IClientSupervision) => void;
  bayActiveAtDateTime: Bays | undefined;
  isBay6: boolean;
  cohortAssignmentsLoading: boolean;
}

function ClientSupervisionInfo(props: ClientSupervisionInfoProps) {
  const { clientSupervision, onUpdate, bayActiveAtDateTime, isBay6, cohortAssignmentsLoading } =
    props;
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  // Check if client SN is editable and clientSupervision is not created
  const isEditable = useClientSupervisionEditable() || !clientSupervision?.id;
  const { user } = useSelector((state: RootState) => state.auth);
  const { list: providerAssignments = [], loading: providerAssignmentLoading } = useSelector(
    (state: RootState) => state.providerAssignment,
  );
  const {
    coAssignedProviders,
    loading: coAssignedProvidersLoading,
    provider,
  } = useSelector((state: RootState) => state.providers);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const automaticBtAssignmentRef = useRef<{ noteId: number | null; primaryId: number } | null>(null);
  const [btParticipantsQueried, setBtParticipantsQueried] = useState<boolean>(false);
  const [selectedSecondaryParticipants, setSelectedSecondaryParticipants] = useState<number[]>([]);
  const [concludedBtParticipants, setConcludedBtParticipants] = useState<
    IClientSupervision['bt_participants'] | null
  >(null);
  const [showConfimDeleteDialog, setShowConfirmDeleteDialog] = useState<boolean>(false);
  const [showConfirmMakeNoteEditableDialog, setShowConfirmMakeNoteEditableDialog] =
    useState<boolean>(false);
  const [showConfirmBackdateNoteDialog, setShowConfirmBackdateNoteDialog] =
    useState<boolean>(false);
  const [showConfirmDownloadDialog, setShowConfirmDownloadDialog] = useState<boolean>(false);
  const [backdatedDate, setBackdatedDate] = useState<Moment | null>(null);
  const showDeleteButton = useMemo(() => {
    return (
      !areUserRolesIn(user?.roles, [Roles.LimitedAccessGuardian]) &&
      clientSupervision?.id &&
      (clientSupervision.status === SupervisionNoteStatus.Started ||
        clientSupervision.status === SupervisionNoteStatus.Editing) &&
      (clientSupervision.bcba?.email === user?.email || user?.roles.includes(Roles.Admin))
    );
  }, [clientSupervision, user]);
  const showButtonToMakeNoteEditable = useMemo(() => {
    return (
      areUserRolesIn(user?.roles, [Roles.Admin]) &&
      clientSupervision?.id &&
      clientSupervision.status === SupervisionNoteStatus.Completed
    );
  }, [clientSupervision, user]);
  const showFormToBackdateNote = useMemo(() => {
    return (
      areUserRolesIn(user?.roles, [Roles.Admin]) &&
      clientSupervision?.id &&
      (clientSupervision.status === SupervisionNoteStatus.Editing ||
        clientSupervision.status === SupervisionNoteStatus.Started)
    );
  }, [clientSupervision, user]);
  const [timeStartValue, setTimeStartValue] = useState<Moment | null>(null);

  const [utcOffsetForBackdate, setUtcOffsetForBackdate] = useState<number | null>(null);

  useEffect(() => {
    // To populate clients dropdown
    if (!areUserRolesIn(user?.roles, [Roles.Bcba]) || !provider?.id || !isEditable) return;
    dispatch(
      providerAssignmentActions.getList({
        getAccessTokenSilently,
        active_only: true,
        provider_id: provider.id,
        clinical_relationship: [
          ClinicalRelationships.HomeBcba,
          ClinicalRelationships.BobbingBcba,
          ClinicalRelationships.SubstituteBCBA,
        ],
      }),
    );
  }, [user, provider?.id, isEditable, clientSupervision?.id]);

  useEffect(() => {
    if (!clientSupervision) {
      resetForm();
      return;
    }

    setValues(clientSupervision);
    setBackdatedDate(moment(clientSupervision?.date_start));
    setUtcOffsetForBackdate(clientSupervision?.utc_offset_date_start || null);

    // Fill timevalue if CSN is created
    if (clientSupervision?.date_start && clientSupervision?.id) {
      const timeValue = moment(clientSupervision?.date_start);
      setTimeStartValue(timeValue);
    }

    if (clientSupervision.bt_participants?.secondary) {
      setSelectedSecondaryParticipants(
        clientSupervision.bt_participants.secondary.map((provider: IProvider) => provider.id),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSupervision]);

  const btParticipants = useMemo(() => {
    if (areUserRolesIn(user?.roles, [Roles.Bcba, Roles.Prbt])) {
      if (!coAssignedProviders) return [];

      return coAssignedProviders;
    } else {
      if (!providerAssignments) return [];

      return providerAssignments
        .filter(
          (providerAssignmentIterator: IProviderAssignment) =>
            providerAssignmentIterator?.clinical_relationship === ClinicalRelationships.Prbt ||
            providerAssignmentIterator?.clinical_relationship === ClinicalRelationships.Vbt,
        )
        .map(
          (providerAssignmentIterator: IProviderAssignment) => providerAssignmentIterator?.provider,
        );
    }
  }, [providerAssignments, coAssignedProviders, user]);

  const secondaryParticipants = useMemo(() => {
    if (isBay6) return [];
    if (!btParticipants || !clientSupervision?.bt_participants?.primary) return [];

    return btParticipants.filter(
      (provider: IProvider) =>
        provider.id !== clientSupervision.bt_participants.primary.id,
    );
  }, [btParticipants, clientSupervision, isBay6]);

  const showNoActiveBtsMessage = useMemo(
    () =>
      shouldShowNoActiveBtsMessage({
        clientSupervision,
        btParticipantsQueried,
        providerAssignmentLoading,
        coAssignedProvidersLoading,
        timeStartValue,
        bayActiveAtDateTime,
        isBay6,
      }),
    [
      clientSupervision,
      btParticipantsQueried,
      providerAssignmentLoading,
      coAssignedProvidersLoading,
      timeStartValue,
      bayActiveAtDateTime,
      isBay6,
    ],
  );

  // This is for automatic updating bt_participants for only one BT (not Bay 6 — primary may stay null)
  useEffect(() => {
    if (isBay6) return;
    if (!btParticipants || btParticipants.length !== 1 || !clientSupervision) {
      return;
    }

    if (
      clientSupervision.status === SupervisionNoteStatus.Completed ||
      clientSupervision.bt_participants?.primary
    )
      return;

    const primaryBt = btParticipants[0];
    if (
      primaryBt?.id == null ||
      (automaticBtAssignmentRef.current?.noteId === (clientSupervision.id ?? null) &&
        automaticBtAssignmentRef.current.primaryId === primaryBt.id)
    )
      return;

    automaticBtAssignmentRef.current = {
      noteId: clientSupervision.id ?? null,
      primaryId: primaryBt.id,
    };
    setFieldValue('bt_participants', {
      primary: primaryBt,
      secondary: [],
    });
    if (!clientSupervision.id) return;

    const updateSupervision = async () => {
      const res = await dispatch(
        clientSupervisionActions.updateSupervision({
          supervisionId: clientSupervision.id,
          data: {
            ...clientSupervision,
            duration: clientSupervision.duration ? clientSupervision.duration : 900,
            utc_offset: getUtcOffset(moment(new Date())),
            bt_participants: {
              primary: primaryBt.id,
              secondary: [],
            },
          },
          getAccessTokenSilently,
        }),
      );

      if (res.error) {
        return;
      }
    };

    updateSupervision();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [btParticipants, clientSupervision, isBay6]);

  const clientOptions = useMemo(() => {
    if (!providerAssignments) return [];

    return providerAssignments
      .filter((pa) => pa.client?.clinical_status === ClinicalStatus.CLINICAL && pa.client.id != null)
      .map((pa: IProviderAssignment) => ({
        id: pa.client.id,
        label: `${pa.client?.first_name} ${pa.client?.last_name}`,
        description:
          pa.clinical_relationship === ClinicalRelationships.HomeBcba
            ? ''
            : pa.clinical_relationship?.split('_')[0],
      }));
  }, [providerAssignments]);

  const formik = useFormik<IClientSupervision>({
    initialValues: {
      client_id: '',
      utc_offset: getUtcOffset(moment(new Date())),
      date_start: null,
      duration: '',
      location: '',
      setting: '',
      participants_names: '',
      participants: {
        parent: false,
        sibling: false,
      },
      bt_participants: {
        primary: null,
        secondary: [],
      },
    },
    onSubmit: async (values) => {
      setSubmitting(true);
      const { payload, error } = await dispatch(
        clientSupervisionActions.createSupervision({
          data: {
            ...values,
            date_start: combineMomentDateTime(moment(values?.date_start), moment(timeStartValue)),
            duration: Number(values.duration),
            bt_participants: {
              primary:
                values.bt_participants?.primary?.id != null
                  ? Number(values.bt_participants.primary.id)
                  : null,
              secondary: values.bt_participants?.secondary?.map(
                (provider: IProvider) => provider.id,
              ),
            },
          },
          getAccessTokenSilently,
        }),
      );
      setSubmitting(false);
      if (error) return;

      if (payload) {
        const toastId = 'clientSupervisionStartedToast';
        toast.success('Client Supervision started', { toastId });
        history.navigate(`/client-supervisions/${payload.id}`);
      }
    },
    validationSchema: clientSupervisionSchema,
  });

  const { values, setFieldValue, setValues, submitForm, resetForm } = formik;

  const selectedClientDetails = useMemo(() => {
    if (!values?.client_id || !providerAssignments?.length) return null;

    return providerAssignments.find((pa) => pa.client.id === values?.client_id)?.client ?? null;
  }, [values?.client_id, providerAssignments]);

  const {
    timeToolTipOpen,
    durationToolTipOpen,
    startBetween10to12,
    endBetween10to12,
    zone,
    isTZIdentified,
    abbreviatedZone,
  } = useProcedureTimeRestrictions(
    moment(values?.date_start),
    values?.duration || 0,
    Boolean(clientSupervision != null && Object.hasOwn(clientSupervision, 'id') && !isEditable),
    providerAssignments.find((cl) => cl.client.id === values?.client_id)?.client
      .street_address_zip ?? null,
  );

  useEffect(() => {
    if (!values?.client_id) return;

    if (!clientSupervision?.active_treatment_id && Boolean(clientSupervision?.id)) {
      const toastId = 'no_active_tp_client';
      toast.error("Client doesn't have an active treatment plan", { toastId });
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSupervision?.active_treatment_id]);

  useEffect(() => {
    const resolvedClientId = resolveClientIdFromFormOrSupervision(
      values?.client_id,
      clientSupervision?.client_id,
    );

    if (resolvedClientId === null) return;

    if (!isEditable) {
      setConcludedBtParticipants(clientSupervision?.bt_participants);
      return;
    } else {
      if (areUserRolesIn(user?.roles, [Roles.Bcba, Roles.AssistantAssessor])) {
        setBtParticipantsQueried(true);
        dispatch(
          providerActions.getCoAssignedProviders({
            clinical_relationship: [Roles.Prbt, Roles.Vbt],
            client_id: resolvedClientId,
            getAccessTokenSilently,
          }),
        );
      } else if (!areUserRolesIn(user?.roles, [Roles.Prbt])) {
        setBtParticipantsQueried(true);
        dispatch(
          providerAssignmentActions.getList({
            getAccessTokenSilently,
            active_only: true,
            client_id: resolvedClientId,
          }),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEditable,
    values?.client_id,
    clientSupervision?.client_id,
    clientSupervision?.bt_participants,
    clientSupervision?.id,
  ]);

  const handleUpdateField = <K extends keyof IClientSupervision>(
    key: K,
    value: IClientSupervision[K],
  ) => {
    if (clientSupervision?.[key] === value) return;

    if (key === 'client_id') {
      onUpdate({
        ...values,
        [key]: value,
        bt_participants: {
          primary: null,
          secondary: [],
        },
      });
    } else {
      onUpdate({
        ...values,
        [key]: value,
      });
    }
    setFieldValue(key, value);
  };

  const handleUpdatePrimaryBtParticipant = (providerId: number) => {
    if (!btParticipants) return;

    handleUpdateField('bt_participants', {
      primary: btParticipants.find((provider: IProvider) => provider.id === providerId),
      secondary: [],
    });
  };

  const handleUpdateSecondaryBtParticipants = (providerId: number, checked: boolean) => {
    let currentSelectedSecondaryParticipants = selectedSecondaryParticipants;
    if (checked) {
      currentSelectedSecondaryParticipants = [...currentSelectedSecondaryParticipants, providerId];
    } else {
      currentSelectedSecondaryParticipants = currentSelectedSecondaryParticipants.filter(
        (id: number) => id !== providerId,
      );
    }
    setSelectedSecondaryParticipants(currentSelectedSecondaryParticipants);
    handleUpdateField('bt_participants', {
      ...clientSupervision?.bt_participants,
      secondary: btParticipants.filter((provider: IProvider) =>
        currentSelectedSecondaryParticipants.includes(provider.id),
      ),
    });
  };

  const handleUpdateSessionTime = async (startDate: Moment) => {
    if (!clientSupervision?.id) return;

    // Update session duration on changing dropdown if new time is different from date_start
    if (moment(clientSupervision?.date_start).isSame(startDate, 'minute')) return;

    handleUpdateField('date_start', startDate);
  };

  const handleDeleteSupervision = async () => {
    if (!clientSupervision?.id) return;
    setShowConfirmDeleteDialog(false);

    const { error } = await dispatch(
      clientSupervisionActions.deleteSupervision({
        supervisionId: clientSupervision.id,
        getAccessTokenSilently,
      }),
    );

    if (error) return;
    history.navigate('/dashboard');
  };

  const handleMakeSupervisionEditable = async () => {
    if (!clientSupervision?.id) return;
    setShowConfirmMakeNoteEditableDialog(false);

    const { error } = await dispatch(
      clientSupervisionActions.makeSupervisionEditable({
        supervisionId: clientSupervision.id,
        getAccessTokenSilently,
      }),
    );

    if (error) return;
    location.reload();
  };

  const handleBackdateSupervision = async () => {
    if (!clientSupervision?.id) return;
    setShowConfirmBackdateNoteDialog(false);

    const { error } = await dispatch(
      clientSupervisionActions.backdateSupervision({
        supervisionId: clientSupervision.id,
        data: {
          date_start: backdatedDate,
          utc_offset_date_start: utcOffsetForBackdate ?? 0,
        },
        getAccessTokenSilently,
      }),
    );

    if (error) return;
    location.reload();
  };

  const handleDateSelect = (value: Moment | null) => {
    if (value === null) return;
    let val = value.seconds(0).milliseconds(0);
    val = timeStartValue ? combineMomentDateTime(val, timeStartValue) : val;

    const now = moment();

    if (val.isAfter(now)) {
      val = moment(); // Set value to current time, if value is in future.
    }
    setFieldValue('date_start', val);
    if (timeStartValue) {
      setTimeStartValue(val);
    }
  };

  const handleTimeSelect = (value: Moment | null) => {
    if (value === null || !values?.date_start) return; // select date before time
    let val = value.seconds(0).milliseconds(0);

    const now = moment();

    if (value.isAfter(now)) {
      val = moment(); // Set value to current time, if value is in future.
    }
    const combinedDateTime = combineMomentDateTime(moment(values.date_start), val);
    setTimeStartValue(combinedDateTime);
    setFieldValue('date_start', combinedDateTime);
  };

  const handleDownloadPDF = async () => {
    if (!clientSupervision?.id) return;
    setShowConfirmDownloadDialog(false);
    const response = await dispatch(
      clientSupervisionActions.downloadPDF({
        supervisionId: clientSupervision.id,
        getAccessTokenSilently,
      }),
    );
    if (response.payload?.document_url) {
      window.location.href = response.payload?.document_url;
    } else if (response.error?.message) {
      toast.error(response.error?.message);
    } else {
      toast.error('Something went wrong while downloading the pdf');
    }
  };

  const startButtonDisabled = isStartClientSupervisionButtonDisabled({
    isFormValid: formik.isValid,
    isFormDirty: formik.dirty,
    isSubmitting: submitting,
    timeStartValue,
    clientSupervision,
    durationToolTipOpen,
    timeToolTipOpen,
  });

  const tooltipTitle = (
    <Box data-testid="tooltipTitle" sx={{ display: 'flex', alignItems: 'center' }}>
      <img
        style={{
          width: '20px',
          height: '20px',
          marginRight: '8px',
          marginTop: '-4px',
          filter: 'brightness(84%) contrast(189%) hue-rotate(145deg) saturate(300%)',
        }}
        src={AttentionIcon}
        alt="attention-icon"
      />
      {startBetween10to12 || endBetween10to12 ? (
        <Typography sx={{ fontSize: '16px', color: FORTA_BLACK }}>
          Attention: Supervisions conducted between{' '}
          <b>
            <span style={{ color: FORTA_ERROR }}>{`10:00 p.m and 12:00 a.m ${
              isTZIdentified ? `in the client’s local time zone (${abbreviatedZone})` : ''
            } `}</span>
            are unlikely to be accepted by the insurance.
          </b>
        </Typography>
      ) : (
        <Typography sx={{ fontSize: '16px', color: FORTA_BLACK }}>
          Attention: Supervisions conducted between{' '}
          <b>
            <span style={{ color: FORTA_ERROR }}>{`12:00 a.m and 4:00 a.m ${
              isTZIdentified ? `in the client’s local time zone (${abbreviatedZone}) ` : ''
            } `}</span>
            will not be accepted by the insurance.
          </b>
        </Typography>
      )}
    </Box>
  );
  return (
    <>
      {(submitting ||
        providerAssignmentLoading ||
        coAssignedProvidersLoading ||
        cohortAssignmentsLoading) && <FProgressBar />}
      <FBoxContainer contentStyle={{ pt: 2 }} sx={{ borderRadius: '4px' }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container alignItems={'center'} rowSpacing={{ xs: 2, lg: 0 }}>
            {areUserRolesIn(user?.roles, [Roles.Admin]) && (
              <Box>
                {showFormToBackdateNote && (
                  <FBoxContainer
                    sx={{
                      flex: 1,
                      borderRadius: '8px',
                      backgroundColor: FORTA_WHITE,
                      boxShadow: 'none',
                      mb: 2,
                    }}
                    headerStyle={{
                      background: 'transparent',
                      borderRadius: 0,
                      boxShadow: 'none',
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <Box mr={3}>
                        <FHAntdDateTimePicker
                          format="MM/DD/YY"
                          inputHeight={40}
                          sx={{
                            boxShadow: 'none',
                          }}
                          BackgroundTypes={BackgroundTypes.Colored}
                          inputReadOnly={true}
                          allowClear={false}
                          value={backdatedDate}
                          disabledDate={disabledFutureDate}
                          onSelect={(value: Moment | null) => {
                            if (value) {
                              setBackdatedDate(value);
                            }
                          }}
                          onChange={(value: Moment | null) => {
                            if (value) {
                              setBackdatedDate(value);
                            }
                          }}
                        />
                      </Box>
                      <FAntdTimePicker
                        inputHeight={40}
                        style={{
                          boxShadow: 'none',
                          backgroundColor: FORTA_WHITE,
                        }}
                        format="h:mm A"
                        allowClear={false}
                        showNow={true}
                        use12Hours
                        coloredBackground
                        value={backdatedDate}
                        onChange={(value: Moment | null) => {
                          if (value && backdatedDate) {
                            setBackdatedDate(
                              backdatedDate
                                .clone()
                                .hour(value.hour())
                                .minute(value.minute())
                                .second(0)
                                .millisecond(0),
                            );
                          }
                        }}
                        onSelect={(value: Moment | null) => {
                          if (value && backdatedDate) {
                            setBackdatedDate(
                              backdatedDate
                                .clone()
                                .hour(value.hour())
                                .minute(value.minute())
                                .second(0)
                                .millisecond(0),
                            );
                          }
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', ml: 3 }}>
                        <FHButton
                          sx={{}}
                          disabled={!areUserRolesIn(user?.roles, [Roles.Admin])}
                          onClick={() => setShowConfirmBackdateNoteDialog(true)}
                        >
                          Backdate Note
                        </FHButton>
                      </Box>
                    </Box>
                  </FBoxContainer>
                )}
              </Box>
            )}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Typography fontWeight="bold" textAlign="left" noWrap mb={1}>
                    Client name
                  </Typography>
                  <FHAutocomplete
                    data-testid="clientName"
                    sx={{ flexGrow: 1, boxShadow: 'none' }}
                    inputHeight={40}
                    placeholder="Select client"
                    clearIcon={false}
                    disabled={Boolean(clientSupervision?.id)}
                    isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IOption) => {
                      return (
                        <Box
                          {...props}
                          sx={{ display: 'flex', justifyContent: 'space-between' }}
                          key={option.id}
                          data-testid={`clientLabel${option.id}`}
                        >
                          <Typography>{option.label}</Typography>
                          {option.description && (
                            <Typography
                              sx={{
                                px: 1,
                                ml: 1,
                                borderRadius: '4px',
                                color: FORTA_WHITE,
                                fontWeight: 700,
                                backgroundColor: FORTA_BLUE.light1,
                              }}
                            >
                              {capitalizeFirstLetter(option.description)}
                            </Typography>
                          )}
                        </Box>
                      );
                    }}
                    options={clientOptions ?? []}
                    value={
                      clientOptions.length > 0 && values?.client_id
                        ? clientOptions?.find((cl) => cl.id === values.client_id)?.label
                        : clientSupervision?.client
                          ? `${clientSupervision?.client?.first_name} ${clientSupervision?.client?.last_name}`
                          : ''
                    }
                    onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
                      if (newValue?.id != null) handleUpdateField('client_id', Number(newValue.id));
                    }}
                  />
                </Grid>
                <Grid item xs={2.25}>
                  <Typography fontWeight="bold" textAlign="left" mb={1}>
                    Date
                  </Typography>
                  <FHAntdDateTimePicker
                    data-testid="startDate"
                    inputHeight={40}
                    sx={{ boxShadow: 'none', width: '100%' }}
                    format="MM/DD/YY"
                    allowClear={false}
                    showNow={true}
                    use12Hours
                    iconWidth={16}
                    iconHeight={16}
                    disabled={
                      clientSupervision != null &&
                      Object.hasOwn(clientSupervision, 'id') &&
                      !isEditable
                    }
                    disabledDate={(current: Moment) => disabledDatesOutsideRange(current, -3, 0)}
                    value={values?.date_start ? moment(values?.date_start) : null}
                    onBlur={(_: any) =>
                      handleUpdateSessionTime(
                        timeStartValue
                          ? combineMomentDateTime(
                              moment(values?.date_start),
                              moment(timeStartValue),
                            )
                          : moment(values?.date_start),
                      )
                    }
                    onOk={(newValue: Moment) =>
                      handleUpdateSessionTime(
                        timeStartValue
                          ? combineMomentDateTime(moment(newValue), moment(timeStartValue))
                          : moment(newValue),
                      )
                    }
                    onChange={handleDateSelect}
                    onSelect={handleDateSelect}
                  />
                </Grid>
                <Grid item xs={2.25}>
                  <Typography fontWeight="bold" textAlign="left" mb={1}>
                    Time
                  </Typography>
                  <FHTooltip
                    sx={{ inset: 'auto auto 6px 0px' }}
                    title={tooltipTitle}
                    arrow
                    open={timeToolTipOpen || startBetween10to12}
                    placement="top"
                    maxwidth={400}
                    tooltipBorderRadius="8px"
                    tooltipbackground={FORTA_WHITE}
                    tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
                  >
                    <span>
                      <FAntdTimePicker
                        data-testid="startTime"
                        inputHeight={40}
                        style={{ boxShadow: 'none', width: '100%' }}
                        format="h:mm A"
                        allowClear={false}
                        showNow={true}
                        coloredBackground={false}
                        use12Hours
                        disabled={
                          (clientSupervision != null &&
                            Object.hasOwn(clientSupervision, 'id') &&
                            !isEditable) ||
                          !values?.date_start
                        }
                        disabledTime={(_: Moment) =>
                          disabledFutureHoursAndMinutes(
                            combineMomentDateTime(
                              moment(values?.date_start),
                              moment(timeStartValue ?? undefined),
                            ),
                          )
                        }
                        value={timeStartValue ? moment(timeStartValue) : null}
                        onBlur={(_: any) =>
                          handleUpdateSessionTime(
                            timeStartValue
                              ? combineMomentDateTime(
                                  moment(values?.date_start),
                                  moment(timeStartValue),
                                )
                              : moment(values?.date_start),
                          )
                        }
                        onOk={(newValue: Moment) =>
                          handleUpdateSessionTime(
                            timeStartValue
                              ? combineMomentDateTime(moment(newValue), moment(timeStartValue))
                              : moment(newValue),
                          )
                        }
                        onChange={handleTimeSelect}
                        onSelect={handleTimeSelect}
                        error={timeToolTipOpen || startBetween10to12}
                      />
                    </span>
                  </FHTooltip>
                </Grid>
                <Grid item xs={2.25}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography fontWeight="bold" textAlign="left" mb={1}>
                      Client time
                    </Typography>
                    <FHTooltip
                      sx={{ inset: 'auto auto -6px 0px' }}
                      title={
                        values?.client_id
                          ? `Client's timezone is determined by their zip code ${
                              selectedClientDetails
                                ? `(Client zip code: ${selectedClientDetails.street_address_zip})`
                                : ''
                            }`
                          : 'Select a client to determine their timezone'
                      }
                      arrow
                      placement="top"
                      maxwidth={340}
                      tooltipBorderRadius="8px"
                      tooltipbackground={FORTA_WHITE}
                      tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
                    >
                      <Box sx={{ ml: 0.75 }}>
                        <InfoIcon
                          sx={{
                            color: FORTA_BLUE.primary,
                            width: 21,
                            height: 21,
                          }}
                        />
                      </Box>
                    </FHTooltip>
                  </Box>
                  <ClientTimezoneDisplay
                    dateTime={
                      timeStartValue
                        ? isTZIdentified
                          ? combineMomentDateTime(
                              moment(values?.date_start),
                              moment(timeStartValue),
                            )
                          : null
                        : null
                    }
                    sx={{ backgroundColor: FORTA_WHITE }}
                    zone={zone}
                    message={
                      values?.client_id
                        ? values?.client_id && !isTZIdentified
                          ? 'Timezone undetermined'
                          : 'Select date & time first'
                        : 'Select a client first'
                    }
                    abbreviatedZone={abbreviatedZone}
                    selectedClientId={isTZIdentified ? values?.client_id : null}
                  />
                </Grid>
                <Grid item xs={2.25}>
                  <Typography fontWeight="bold" textAlign="left" mb={1}>
                    Duration
                  </Typography>
                  <FHTooltip
                    sx={{ inset: 'auto auto 10px 0px' }}
                    title={tooltipTitle}
                    arrow
                    open={durationToolTipOpen || endBetween10to12}
                    placement="top"
                    maxwidth={400}
                    tooltipBorderRadius="8px"
                    tooltipbackground={FORTA_WHITE}
                    tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
                  >
                    <span>
                      <FHSelect
                        data-testid="duration"
                        sx={{ width: '100%', boxShadow: 'none' }}
                        inputHeight={40}
                        disabled={
                          clientSupervision != null &&
                          Object.hasOwn(clientSupervision, 'id') &&
                          !isEditable
                        }
                        renderValue={
                          !values?.duration
                            ? () => <Typography>Length of session</Typography>
                            : undefined
                        }
                        options={CLIENT_SN_DURATION_OPTIONS}
                        value={values?.duration || ''}
                        onUpdate={(value: SelectValueType) =>
                          handleUpdateField('duration', value === '' ? '' : Number(value))
                        }
                        error={durationToolTipOpen || endBetween10to12}
                      />
                    </span>
                  </FHTooltip>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} mt={3}>
              <Grid container spacing={3}>
                <Grid item xs={1.5}>
                  <Typography fontWeight="bold" textAlign="left" mb={1}>
                    Client location
                  </Typography>
                  <FHSelect
                    data-testid="selectLocation"
                    sx={{ width: '100%', boxShadow: 'none' }}
                    inputHeight={40}
                    disabled={
                      clientSupervision != null &&
                      Object.hasOwn(clientSupervision, 'id') &&
                      !isEditable
                    }
                    options={SUPERVISION_LOCATION_V2}
                    value={values.location || ''}
                    renderValue={(value: any) => {
                      return capitalizeFirstLetter(value.toLowerCase());
                    }}
                    onUpdate={(value: SelectValueType) =>
                      handleUpdateField('location', String(value))
                    }
                  />
                </Grid>
                <Grid item xs={1.5}>
                  <Typography fontWeight="bold" textAlign="left" mb={1}>
                    Delivery method
                  </Typography>
                  <FHSelect
                    data-testid="setting"
                    sx={{ width: '100%', boxShadow: 'none' }}
                    inputHeight={40}
                    disabled={
                      clientSupervision != null &&
                      Object.hasOwn(clientSupervision, 'id') &&
                      !isEditable
                    }
                    options={SUPERVISION_SETTING_V2}
                    value={clientSupervision?.setting || values.setting || ''}
                    renderValue={(value: any) => {
                      return value
                        ? capitalizeFirstLetter(value.toLowerCase())
                        : 'Select delivery method';
                    }}
                    onUpdate={(value: SelectValueType) =>
                      handleUpdateField('setting', String(value))
                    }
                    selectFirstOption={false}
                  />
                </Grid>
                {values?.status === SupervisionNoteStatus.Completed &&
                !isBay6 &&
                !values?.bt_participants?.primary &&
                values?.participants_names &&
                values?.participants_names.length > 0 ? (
                  <>
                    <Grid item xs={3}>
                      <Typography fontWeight="bold" textAlign="left" mb={1}>
                        Participants
                      </Typography>
                      <Box sx={{ display: 'flex', width: '100%', height: 40 }}>
                        <FCheckbox
                          data-testid="participantParentCheckbox"
                          sx={{ mr: 1 }}
                          iconStyles={{ boxShadow: 'none' }}
                          iconSize={{ width: 24, height: 24 }}
                          label={'Parent'}
                          disabled={
                            clientSupervision != null &&
                            Object.hasOwn(clientSupervision, 'id') &&
                            !isEditable
                          }
                          checked={Boolean(values?.participants?.parent)}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleUpdateField('participants', {
                              ...values?.participants,
                              parent: e.target.checked,
                            })
                          }
                        />
                        <FCheckbox
                          data-testid="participantSiblingCheckbox"
                          sx={{ mr: 1 }}
                          iconSize={{ width: 24, height: 24 }}
                          iconStyles={{ boxShadow: 'none' }}
                          label={'Sibling'}
                          disabled={
                            clientSupervision != null &&
                            Object.hasOwn(clientSupervision, 'id') &&
                            !isEditable
                          }
                          checked={Boolean(values?.participants?.sibling)}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleUpdateField('participants', {
                              ...values?.participants,
                              sibling: e.target.checked,
                            })
                          }
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography fontWeight="bold" textAlign="left" mb={1}>
                        Name of participants
                      </Typography>
                      <FHInputField
                        data-testid="nameOfParticipants"
                        sx={{ flex: 1, width: '100%', boxShadow: 'none' }}
                        inputHeight={40}
                        colorTheme={Themes.Primary}
                        disabled={
                          clientSupervision != null &&
                          Object.hasOwn(clientSupervision, 'id') &&
                          !isEditable
                        }
                        value={values?.participants_names || ''}
                        onBlur={() =>
                          handleUpdateField('participants_names', values?.participants_names)
                        }
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          setFieldValue('participants_names', e.target.value);
                        }}
                      />
                    </Grid>
                  </>
                ) : (
                  <BehavioralTechnicianSection
                    isBay6={isBay6}
                    bay6EditModeProvider={user?.given_name + ' ' + user?.family_name}
                    bay6ViewModeProvider={
                      clientSupervision?.bcba?.first_name + ' ' + clientSupervision?.bcba?.last_name
                    }
                    isEditable={isEditable}
                    concludedBtParticipants={concludedBtParticipants}
                    btParticipants={btParticipants}
                    secondaryParticipants={secondaryParticipants}
                    primaryParticipantId={values?.bt_participants?.primary?.id ?? ''}
                    selectedSecondaryParticipantIds={selectedSecondaryParticipants}
                    participantSelectionDisabled={Boolean(clientSupervision?.id) && !isEditable}
                    showNoActiveBtsMessage={showNoActiveBtsMessage}
                    onPrimaryParticipantChange={handleUpdatePrimaryBtParticipant}
                    onSecondaryParticipantChange={handleUpdateSecondaryBtParticipants}
                  />
                )}
                <Grid
                  item
                  xs={1}
                  display={'flex'}
                  alignItems={'center'}
                  gap={2}
                  justifyContent={'flex-end'}
                >
                  {showButtonToMakeNoteEditable && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <FHButton onClick={() => setShowConfirmMakeNoteEditableDialog(true)}>
                        Edit
                      </FHButton>
                    </Box>
                  )}
                  {!areUserRolesIn(user?.roles, [Roles.Prbt, Roles.Vbt]) &&
                  clientSupervision?.status === SupervisionNoteStatus.Completed ? (
                    <IconButton
                      data-testid="downloadPDF"
                      onClick={() => setShowConfirmDownloadDialog(true)}
                    >
                      <DownloadIcon color={FORTA_BLUE.primary} width={24} height={24} />
                    </IconButton>
                  ) : (
                    <FHButton
                      data-testid="startNotes"
                      sx={{ width: '100px' }}
                      disabled={startButtonDisabled}
                      onClick={submitForm}
                    >
                      Start
                    </FHButton>
                  )}
                  {showDeleteButton && (
                    <IconButton
                      sx={{ p: 2 }}
                      data-testid="deleteClientSN"
                      onClick={() => setShowConfirmDeleteDialog(true)}
                    >
                      <TrashIcon color={FORTA_BLUE.primary} width={24} height={24} />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <FHConfirmDialog
              open={showConfimDeleteDialog}
              colorTheme={Themes.Primary}
              title={'Delete client supervision'}
              content={
                'Are you sure you want to delete this client supervision? This action cannot be undone.'
              }
              submittext={'Yes, delete'}
              canceltext={'Cancel'}
              submitBtnTestId={'deleteBtn'}
              cancelBtnTestId={'cancelBtn'}
              onClose={() => setShowConfirmDeleteDialog(false)}
              onSubmit={handleDeleteSupervision}
            />
            <FHConfirmDialog
              open={showConfirmMakeNoteEditableDialog}
              colorTheme={Themes.Primary}
              title={'Make editable'}
              content={'Are you sure you want to make this client supervision note editable?'}
              submittext={'Yes'}
              canceltext={'Cancel'}
              onClose={() => setShowConfirmMakeNoteEditableDialog(false)}
              onSubmit={handleMakeSupervisionEditable}
            />
            <FHConfirmDialog
              open={showConfirmBackdateNoteDialog}
              colorTheme={Themes.Primary}
              title={'Change'}
              content={
                'Are you sure you want to change the start date of this client supervision note?'
              }
              submittext={'Yes'}
              canceltext={'Cancel'}
              onClose={() => setShowConfirmBackdateNoteDialog(false)}
              onSubmit={handleBackdateSupervision}
            />
            <FHConfirmDialog
              open={showConfirmDownloadDialog}
              colorTheme={Themes.Primary}
              title={'Download client supervision'}
              content={'Are you sure you want to download this client supervision?'}
              submittext={'Download'}
              canceltext={'Cancel'}
              onClose={() => setShowConfirmDownloadDialog(false)}
              onSubmit={handleDownloadPDF}
            />
          </Grid>
        </form>
      </FBoxContainer>
    </>
  );
}

export default ClientSupervisionInfo;
